import React, {useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/dist/query';
import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {useCurrentUserState} from 'app/current-user/api';
import {createFormErrorsContext, FormErrorsProps} from 'app/general/forms';
import {useSignoffDocument, useSignoffs} from 'app/signoffs/api';
import {gt} from 'app/utils/i18n';

import type {
  DocumentContentType,
  DocumentVersionContentType,
} from 'app/acceptance/types';

const StyledContainerForm = styled.form`
  background: ${colors.lightBg};
  border-top: 4px solid ${colors.linkBlue};
  border-radius: 4px;
`;

interface Props extends FormErrorsProps {
  objectName: string;
  objectId: number;
  contentType: DocumentContentType;
  versionId: number;
  versionContentType: DocumentVersionContentType;
}

const BaseSignoffForm = ({
  objectId,
  contentType,
  objectName,
  formErrors,
  versionId,
  versionContentType,
  captureFormErrorsFromRejection,
}: Props) => {
  const currentUser = useCurrentUserState().data;
  const [signoffDocument, {isLoading: isSigningoff}] = useSignoffDocument();
  const {data: mySignoffs} = useSignoffs(
    currentUser == null
      ? skipToken
      : {
          user_id: currentUser.id,
          object_id: objectId,
          content_type: contentType,
          filter: 'only_unsigned',
        }
  );

  // Extracting the unsigned signoff from the array as the response from backend is wrapped in an array.
  const [signoff] = mySignoffs?.results ?? [null];

  const [text, setText] = useState<string>('');

  if (signoff == null || signoff.signed_at != null) {
    return null;
  }

  const handleSignoff = (e) => {
    e.preventDefault();
    signoffDocument({
      url: signoff.url,
      signed_text: text,
      version_id: versionId,
      version_content_type: versionContentType,
    })
      .unwrap()
      .catch(captureFormErrorsFromRejection);
  };

  const name = (
    <input
      value={text}
      type="text"
      className="form-control form-control-sm"
      placeholder={currentUser?.name ?? gt('Enter your name')}
      onChange={(e) => setText(e.target.value)}
    />
  );
  const docName = <strong>{objectName}</strong>;
  return (
    <StyledContainerForm className="p-3 d-print-none" onSubmit={handleSignoff}>
      <div className="form-inline">
        <div className="mb-3 pb-3 border-bottom w-100">
          {gt('I %(name)s have read and understood %(docName)s.', {
            name,
            docName,
          })}
          {formErrors['signed_text'] != null
            ? formErrors['signed_text'].map((message, index) => (
                <div className="text-danger small mt-1" key={index}>
                  {message}
                </div>
              ))
            : null}
        </div>
      </div>
      <div className="text-right">
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={isSigningoff}
        >
          <i
            className={cs(
              'mr-1 fa',
              isSigningoff ? 'fa-circle-notch fa-spin' : 'fa-signature'
            )}
          />
          {gt('Sign Now')}
        </button>
      </div>
    </StyledContainerForm>
  );
};

export const SignoffForm = createFormErrorsContext<Props>(BaseSignoffForm);
